import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TENANT_PATHS } from '../../../routing/paths.ts';
import { useTheme } from '../../../contexts/ThemeContext.tsx';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';
import { useCreateRisk } from '../../../hooks/risks/useRisks.ts';
import { riskManualCreateSchema, type RiskFormValues } from '../schema.ts';
import { RiskFormFields, defaultRiskFormValues } from './RiskFormFields.tsx';
import { RiskBulkUploadSection } from './RiskBulkUploadSection.tsx';

const RISK_CREATE_MODE = {
  MANUAL: 'manual',
  UPLOAD: 'upload',
} as const;
type RiskCreateMode = (typeof RISK_CREATE_MODE)[keyof typeof RISK_CREATE_MODE];

export const RiskCreatePanel: React.FC = () => {
  const navigate = useNavigate();
  const { primaryColor } = useTheme();
  const { selectedAuditYearId } = useAuditYear();
  const [createMode, setCreateMode] = useState<RiskCreateMode>(RISK_CREATE_MODE.MANUAL);
  const createRiskMutation = useCreateRisk();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<RiskFormValues>({
    resolver: yupResolver(riskManualCreateSchema),
    defaultValues: defaultRiskFormValues,
  });

  const onSubmit = async (values: RiskFormValues) => {
    try {
      // Inject the active audit year so the new risk is year-scoped like all other modules.
      await createRiskMutation.mutateAsync({
        ...values,
        auditYearId: selectedAuditYearId ?? undefined,
      });
      navigate(TENANT_PATHS.SOX_RISKS);
    } catch {
      // Toast is handled inside the mutation hook; keep the form open for correction/retry.
    }
  };

  const primaryButtonStyle = primaryColor
    ? { backgroundColor: primaryColor, borderColor: primaryColor }
    : undefined;
  const isSaving = createRiskMutation.isPending;

  return (
    <div>
      <div>
        {createMode === RISK_CREATE_MODE.MANUAL ? (
          <>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <RiskFormFields
                register={register}
                control={control}
                errors={errors}
                setValue={setValue}
                clearErrors={clearErrors}
                showClassificationFields
                detailsHeaderSlot={(
                  <>
                    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
                      <button
                        type="button"
                        onClick={() => setCreateMode(RISK_CREATE_MODE.MANUAL)}
                        className="px-4 py-2 text-sm font-medium rounded-md transition-colors bg-white text-gray-900 shadow-sm"
                      >
                        Manual
                      </button>
                      <button
                        type="button"
                        onClick={() => setCreateMode(RISK_CREATE_MODE.UPLOAD)}
                        className="px-4 py-2 text-sm font-medium rounded-md transition-colors text-gray-500 hover:text-gray-700"
                      >
                        Bulk Upload
                      </button>
                    </div>
                    <h2 className="text-base font-semibold text-gray-900 pt-2">Create Risk Manually</h2>
                    <p className="text-sm text-gray-500">
                      Enter risk details directly. The risk will be saved against the current SOX scope.
                    </p>
                  </>
                )}
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => navigate(TENANT_PATHS.SOX_RISKS)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={primaryButtonStyle}
                >
                  {isSaving ? 'Saving...' : 'Save risk'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex w-fit items-center gap-1 rounded-lg bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setCreateMode(RISK_CREATE_MODE.MANUAL)}
                className="rounded-md px-4 py-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
              >
                Manual
              </button>
              <button
                type="button"
                onClick={() => setCreateMode(RISK_CREATE_MODE.UPLOAD)}
                className="rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-900 shadow-sm transition-colors"
              >
                Bulk Upload
              </button>
            </div>
            <RiskBulkUploadSection />
          </div>
        )}
      </div>
    </div>
  );
};

export default RiskCreatePanel;
